import React, { useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Camera, X, Paperclip, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * receipts: array of {id, filename, content_type}
 * onChange: (newReceipts) => void
 * onOcr: optional callback fired once per uploaded image — receives the parsed
 *        {vendor, amount, date, category_hint, confidence} so the parent form
 *        can auto-fill empty fields.
 */
export default function ReceiptUploader({ receipts = [], onChange, testidPrefix = "receipt", onOcr }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      }
      onChange([...(receipts || []), ...uploaded]);
      toast.success(`${uploaded.length} receipt${uploaded.length > 1 ? "s" : ""} attached`);

      // Auto-OCR the first uploaded image (most camera shots are single).
      if (onOcr) {
        const firstImage = uploaded.find((u) => (u.content_type || "").startsWith("image/"));
        if (firstImage) {
          setOcrBusy(true);
          try {
            const { data: ocr } = await api.post(`/receipts/${firstImage.id}/ocr`);
            if (ocr && (ocr.vendor || ocr.amount > 0 || ocr.date)) {
              onOcr(ocr);
              toast.success("Auto-filled from receipt photo");
            }
          } catch {
            // Silent — user can still fill the form manually.
          } finally {
            setOcrBusy(false);
          }
        }
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const remove = (id) => {
    onChange(receipts.filter((r) => r.id !== id));
  };

  return (
    <div data-testid={`${testidPrefix}-uploader`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">
        Receipt Photos
      </div>
      <div className="flex flex-wrap gap-2 items-start">
        {(receipts || []).map((r) => {
          const isPdf = (r.content_type || "").includes("pdf") || (r.filename || "").toLowerCase().endsWith(".pdf");
          return (
            <div
              key={r.id}
              className="relative w-20 h-20 border border-[#1F1E1B] bg-[#F5F4F0] overflow-hidden group"
              data-testid={`${testidPrefix}-thumb-${r.id}`}
            >
              {isPdf ? (
                <a
                  href={`${API}/files/${r.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-full flex flex-col items-center justify-center text-[#1F1E1B] hover:bg-[#EEECE4]"
                  title={r.filename}
                >
                  <span className="text-[10px] font-bold tracking-widest">PDF</span>
                  <span className="text-[9px] text-[#66645E] mt-0.5 truncate max-w-[60px]">{r.filename}</span>
                </a>
              ) : (
                <img
                  src={`${API}/files/${r.id}`}
                  alt={r.filename}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setPreview(r)}
                />
              )}
              <button
                type="button"
                onClick={() => remove(r.id)}
                className="absolute top-0 right-0 bg-[#BC4727] text-white w-5 h-5 flex items-center justify-center hover:bg-[#9D391E]"
                data-testid={`${testidPrefix}-remove-${r.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          data-testid={`${testidPrefix}-camera-btn`}
          title="Take a photo with your camera"
          className="w-20 h-20 border-2 border-[#1F1E1B] bg-[#1F1E1B] text-white flex flex-col items-center justify-center text-[10px] font-bold uppercase tracking-wider hover:bg-[#BC4727] hover:border-[#BC4727] transition-colors"
        >
          {uploading ? (
            <span className="text-[10px]">Uploading…</span>
          ) : (
            <>
              <Camera className="w-5 h-5 mb-1" strokeWidth={2.4} />
              Take Photo
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testidPrefix}-add-btn`}
          title="Pick a photo or PDF from your device"
          className="w-20 h-20 border-2 border-dashed border-[#1F1E1B] flex flex-col items-center justify-center text-[10px] font-bold uppercase tracking-wider text-[#66645E] hover:bg-[#F5F4F0] hover:text-[#1F1E1B] transition-colors"
        >
          {uploading ? (
            <span className="text-[10px]">Uploading…</span>
          ) : (
            <>
              <ImagePlus className="w-5 h-5 mb-1" strokeWidth={2.2} />
              Choose File
            </>
          )}
        </button>
        <input
          ref={cameraRef}
          data-testid={`${testidPrefix}-camera-input`}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
        <input
          ref={fileRef}
          data-testid={`${testidPrefix}-file-input`}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </div>
      {(receipts || []).length > 0 && (
        <div className="text-[11px] text-[#66645E] mt-2 flex items-center gap-1">
          <Paperclip className="w-3 h-3" /> {receipts.length} attached · photos open inline, PDFs open in new tab
        </div>
      )}
      {(receipts || []).length === 0 && (
        <div className="text-[11px] text-[#66645E] mt-2">
          Snap a receipt on the spot or upload an existing photo/PDF.{onOcr ? " The form auto-fills vendor + amount from the photo." : ""}
        </div>
      )}
      {ocrBusy && (
        <div className="text-[11px] text-[#1A4135] mt-1 font-semibold animate-pulse" data-testid={`${testidPrefix}-ocr-status`}>
          Reading the receipt…
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="receipt-lightbox"
        >
          <div className="max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2 text-white">
              <div className="text-sm font-semibold truncate max-w-md">{preview.filename}</div>
              <button onClick={() => setPreview(null)} className="text-white text-2xl leading-none px-2" data-testid="lightbox-close">×</button>
            </div>
            <img
              src={`${API}/files/${preview.id}`}
              alt={preview.filename}
              className="max-w-full max-h-[80vh] border-2 border-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function ReceiptBadge({ receipts, onClick, testid }) {
  if (!receipts?.length) return <span className="text-[#D2CEC1]">—</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1 border border-[#1F1E1B] px-2 py-0.5 text-xs font-bold hover:bg-[#1F1E1B] hover:text-white transition-colors"
    >
      <Paperclip className="w-3 h-3" /> {receipts.length}
    </button>
  );
}

export function ReceiptGallery({ receipts, onClose }) {
  const [preview, setPreview] = useState(null);
  if (!receipts?.length) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-6" onClick={onClose} data-testid="receipt-gallery">
      <div className="bg-white border-2 border-[#1F1E1B] max-w-3xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#1F1E1B] px-5 py-3 bg-[#F5F4F0]">
          <h2 className="font-display text-lg font-bold">Attached Receipts ({receipts.length})</h2>
          <button onClick={onClose} className="text-[#66645E] text-xl leading-none px-2" data-testid="gallery-close">×</button>
        </div>
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {receipts.map((r) => {
            const isPdf = (r.content_type || "").includes("pdf") || (r.filename || "").toLowerCase().endsWith(".pdf");
            if (isPdf) {
              return (
                <a
                  key={r.id}
                  href={`${API}/files/${r.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#1F1E1B] aspect-square flex flex-col items-center justify-center bg-[#F5F4F0] hover:bg-[#EEECE4]"
                >
                  <span className="font-display text-2xl font-black">PDF</span>
                  <span className="text-xs text-[#66645E] mt-2 px-2 truncate max-w-full">{r.filename}</span>
                </a>
              );
            }
            return (
              <button key={r.id} type="button" onClick={() => setPreview(r)} className="border border-[#1F1E1B] aspect-square overflow-hidden">
                <img src={`${API}/files/${r.id}`} alt={r.filename} className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <img src={`${API}/files/${preview.id}`} alt={preview.filename} className="max-w-full max-h-[90vh] border-2 border-white" />
        </div>
      )}
    </div>
  );
}
